import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const BookingSettingsSection = ({ userProfile, expandedSection, onAccordionChange, setSuccess, setError, currentUser, isMobile }) => {
  const [bookingSettings, setBookingSettings] = useState({
    minAdvanceHours: userProfile.bookingSettings?.minAdvanceHours || 2,
    maxAdvanceDays: userProfile.bookingSettings?.maxAdvanceDays || 30,
    allowSameDayBooking: userProfile.bookingSettings?.allowSameDayBooking ?? true,
    autoConfirmBookings: userProfile.bookingSettings?.autoConfirmBookings ?? false,
    cancellationPolicy: userProfile.bookingSettings?.cancellationPolicy || 24,
    ...userProfile.bookingSettings
  });
  const [loading, setLoading] = useState(false);

  const handleBookingSettingChange = (field, value) => {
    setBookingSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveBookingSettings = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        bookingSettings: bookingSettings,
        updatedAt: new Date()
      });
      
      setSuccess('Paramètres de réservation mis à jour!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating booking settings:', error);
      setError('Erreur lors de la mise à jour des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#F5F5DC',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB'
  };

  const content = (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 4 } }}>
      {!isMobile && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary,
            mb: { xs: 2, md: 3 },
            fontSize: { xs: '1.125rem', md: '1.25rem' }
          }}
        >
          Paramètres de réservation
        </Typography>
      )}

      <Typography 
        variant="body2" 
        sx={{ 
          color: colors.secondary, 
          mb: 3,
          lineHeight: 1.6,
          fontSize: { xs: '0.875rem', md: '1rem' }
        }}
      >
        Configurez les règles de réservation de votre salon
      </Typography>

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Préavis minimum (heures)"
          type="number"
          value={bookingSettings.minAdvanceHours}
          onChange={(e) => handleBookingSettingChange('minAdvanceHours', parseInt(e.target.value))}
          helperText="Nombre d'heures minimum avant le rendez-vous"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: colors.primary,
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }
          }}
        />

        <TextField
          fullWidth
          label="Réservation maximum à l'avance (jours)"
          type="number"
          value={bookingSettings.maxAdvanceDays}
          onChange={(e) => handleBookingSettingChange('maxAdvanceDays', parseInt(e.target.value))}
          helperText="Nombre de jours maximum pour réserver à l'avance"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: colors.primary,
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }
          }}
        />

        <TextField
          fullWidth
          label="Politique d'annulation (heures)"
          type="number"
          value={bookingSettings.cancellationPolicy}
          onChange={(e) => handleBookingSettingChange('cancellationPolicy', parseInt(e.target.value))}
          helperText="Délai d'annulation gratuite avant le rendez-vous"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: colors.primary,
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }
          }}
        />

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={bookingSettings.allowSameDayBooking}
                onChange={(e) => handleBookingSettingChange('allowSameDayBooking', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Autoriser les réservations le jour même
              </Typography>
            }
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={bookingSettings.autoConfirmBookings}
                onChange={(e) => handleBookingSettingChange('autoConfirmBookings', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Confirmation automatique des réservations
              </Typography>
            }
          />
        </Box>

        <Button
          variant="contained"
          onClick={saveBookingSettings}
          disabled={loading}
          fullWidth
          sx={{
            backgroundColor: colors.primary,
            color: colors.surface,
            py: { xs: 1.5, md: 2 },
            fontSize: { xs: '0.875rem', md: '1rem' },
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: '#333333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          {loading ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
        </Button>
      </Stack>
    </Box>
  );

  if (isMobile) {
    return (
      <Accordion 
        expanded={expandedSection === 'booking'} 
        onChange={onAccordionChange('booking')}
        elevation={0}
        sx={{ 
          '&:before': { display: 'none' },
          borderRadius: '0 !important'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            px: 3,
            py: 2,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              '&.Mui-expanded': {
                margin: 0
              }
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: colors.primary,
              fontSize: '1.125rem'
            }}
          >
            Paramètres de réservation
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {content}
        </AccordionDetails>
      </Accordion>
    );
  }

  return content;
};

export default BookingSettingsSection;